<template>
  <v-card-text class="card-bg-2 pa-0 default-border-bottom scroll-border-bottom">
    <v-switch v-model="showAnalyteName" label="Show Envirodata Analyte Name" color="primary" hide-details class="switch"></v-switch>
    <v-menu offset-y open-on-hover :open-delay="400" :close-delay="400">
      <template v-slot:activator="{ on }">
        <v-icon v-on="on" class="clickable ml-2" color="primary">
          info_outline
        </v-icon>
      </template>
      <v-card max-width="500">
        <v-card-text>
          Internal EnviroData names are unique analyte names that help us manage analytes and potential synonyms.
          Display names are not unique and the internal EnviroData name will provide an indication of the difference
          between repeating display names.
        </v-card-text>
      </v-card>
    </v-menu>
    <v-divider />
    <v-layout class="default-border-bottom scroll-border-bottom">
      <v-flex>
        <v-item-group mandatory v-model="filterToggle" active-class="white--text">
          <v-subheader style="display: inline; cursor: default;">Show:</v-subheader>
          <v-item>
            <v-chip small slot-scope="{ active, toggle }" :color="active ? 'primary' : 'default'" :selected="active" class="chipFilter" @click="toggle">Available</v-chip>
          </v-item>
          <v-item>
            <v-chip small slot-scope="{ active, toggle }" :color="active ? 'primary' : 'default'" :selected="active" class="chipFilter" @click="toggle">
              <div style="float: left;" class="pr-2" :class="{ 'info--text font-weight-bold': selected.length > 0 }">
                {{ selected.length }}</div>
              Selected
            </v-chip>
          </v-item>
        </v-item-group>
      </v-flex>
      <v-spacer />
      <v-flex class="pr-2"></v-flex>
    </v-layout>
    <v-data-table class="analytes-table" :class="{ 'five-items': pagination.rowsPerPage === 5, 'ten-items': pagination.rowsPerPage === 10, 'twenty-five-items': pagination.rowsPerPage === 25 }" ref="analytesTable" v-model="selected" :search="search" select-all :disabled="disabled" :headers="headers" :items="allAnalytes" :pagination.sync="pagination" :item-key="selectionKey" :custom-filter="customFilter" :custom-sort="customSorter">
      <template v-slot:no-data>
        <div class="pa-4 text-xs-center">
          <v-icon color="warning">warning</v-icon>
          <br />
          No matching analytes found
        </div>
      </template>
      <template v-slot:no-results>
        <div class="pa-4 text-xs-center">
          <v-icon color="warning">warning</v-icon>
          <br />
          No matching analytes found
        </div>
      </template>
      <template v-slot:headers="props">
        <tr>
          <th>
            <v-checkbox :disabled="filterToggle == filterToggles.unavailable || disabled" color="primary" :headers="headers" :item-key="selectionKey" class="analyteTable" :input-value="props.all" :indeterminate="props.indeterminate" primary hide-details @click.stop="toggleAll()"></v-checkbox>
          </th>
          <th v-for="header in props.headers" :key="header.text" :class="[
              'column', header.sortable && 'sortable',
              pagination.descending ? 'desc' : 'asc',
              header.value === pagination.sortBy ? 'active' : ''
            ]" @click="changeSort(header.sortable, header.value)">
            <v-icon small v-if="header.sortable">arrow_upward</v-icon>
            {{ header.text }}
          </th>
        </tr>
      </template>
      <template v-slot:items="props">
        <tr :class="{ 'clickable': (!isUnavailable(props.item) || props.selected) && !disabled }" :active="props.selected" @click="toggleRow(props)">
          <td :class="{
              'align-button-checkbox': filterToggle == filterToggles.selected
            }">
            <v-checkbox v-if="filterToggle != filterToggles.selected" :disabled="(isUnavailable(props.item) && !props.selected) || disabled" color="primary" class="clickable" :input-value="props.selected" primary hide-details></v-checkbox>
            <v-btn v-else :disabled="disabled" large class="ma-0" icon>
              <v-icon color="primary">
                check_box
              </v-icon>
            </v-btn>
          </td>
          <td class="text-xs-center">
            {{ props.item.DisplayName }}
          </td>
          <td v-if="showAnalyteName">{{ props.item.AnalyteName }}</td>
          <td class="text-xs-center">
            {{ formatDate(getValue(props.item, 'FirstSampleDate')) }}
          </td>
          <td class="text-xs-center">
            {{ formatDate(getValue(props.item, 'LastSampleDate')) }}
          </td>
          <td class="text-xs-center">{{ getValue(props.item, 'Count') || 0 }}</td>
        </tr>
      </template>
    </v-data-table>
  </v-card-text>
</template>

<script>
  import {
    prettyDateTimeFormat,
  } from '@/components/mixins/MomentUtil'

  import {
    customSearcher,
    getObjectValues,
  } from '@/helpers/search'

  import {
    mergeDistinctObjectArrays,
  } from '@/helpers/arrays'

  import {
    defaultTableSorter,
    objectSorter,
    stringSorter,
    datetimeSorter,
    numberSorter,
  } from '@/helpers/sorting'

  const filterToggles = {
    available: 0,
    selected: 1,
    unavailable: 2,
    all: -1,
  }

  const itemKey = 'AnalyteName'
  const searchKey = 'AnalyteName'

  export default {
    components: {},
    props: {
      allAnalytes: Array,
      analytes: Object,
      selection: Object,
      search: String,
      index: Number,
      disabled: Boolean,
    },
    data() {
      return {
        selectionKey: itemKey,
        pagination: {
          sortBy: 'AnalyteName',
          rowsPerPage: 10,
          page: 1,
        },
        filterToggles,
        filterToggle: filterToggles.available,
        selected: [],
        itemsMetadata: [],
        showAnalyteName: false,
      }
    },
    computed: {
      missingAnalytes: {
        get() {
          return this.$store.getters['multistation/missingParams'].analytes
        },
        set(analytes) {
          this.$store.dispatch('multistation/updateMissingParams', {
            analytes,
          })
        },
      },
      headers() {
        return this.showAnalyteName ? [{
          text: 'Display Name',
          align: 'center',
          sortable: true,
          value: 'DisplayName',
          sorter: objectSorter('DisplayName', stringSorter),
        },
        {
          text: 'Envirodata Analyte Name',
          align: 'center',
          sortable: true,
          value: 'AnalyteName',
          sorter: objectSorter('AnalyteName', stringSorter),
        },
        {
          text: 'First Record',
          align: 'center',
          sortable: false,
          value: 'FirstSampleDate',
          sorter: objectSorter('FirstSampleDate', datetimeSorter),
        },
        {
          text: 'Last Record',
          align: 'center',
          sortable: false,
          value: 'LastSampleDate',
          sorter: objectSorter('LastSampleDate', datetimeSorter),
        },
        {
          text: '# Records',
          align: 'center',
          sortable: false,
          value: 'Count',
          sorter: objectSorter('Count', numberSorter),
        },
        ] : [{
          text: 'Analyte',
          align: 'center',
          sortable: true,
          value: 'AnalyteName',
          sorter: objectSorter('AnalyteName', stringSorter),
        },
        {
          text: 'First Record',
          align: 'center',
          sortable: false,
          value: 'FirstSampleDate',
          sorter: objectSorter('FirstSampleDate', datetimeSorter),
        },
        {
          text: 'Last Record',
          align: 'center',
          sortable: false,
          value: 'LastSampleDate',
          sorter: objectSorter('LastSampleDate', datetimeSorter),
        },
        {
          text: '# Records',
          align: 'center',
          sortable: false,
          value: 'Count',
          sorter: objectSorter('Count', numberSorter),
        },
        ]
      },
      customSearch() {
        return customSearcher(this.itemsMetadata, itemKey)
      },
      availableAnalytes() {
        return this.allAnalytes.filter(analyte => !this.isUnavailable(analyte))
      },
      customSorter() {
        return defaultTableSorter(this.headers, this.pagination.sortBy, this.pagination.descending)
      },
    },
    watch: {
      allAnalytes() {
        this.update()
      },
      filterToggle() {
        this.pagination.page = 1
      },
      selected(value) {
        this.$emit('analytes', value.map(analyte => {
          let mapping = this.analytes[analyte.AnalyteName]
          if (mapping) return mapping

          return {
            AnalyteName: analyte.AnalyteName,
            StationIds: new Set(),
          }
        }))
      },
    },
    methods: {
      getValue(item, valueKey) {
        let key = item[itemKey]
        let analyte = this.analytes[key]
        if (!analyte) return
        return analyte[valueKey]
      },
      isUnavailable(item) {
        let key = item[itemKey]
        let analyte = this.analytes[key]
        return !analyte
      },
      customFilter(items, index) {
        let searchResult = this.customSearch(items, index)
        let result = []

        if (this.filterToggle == filterToggles.selected) {
          searchResult.forEach(item => {
            let index = this.selected.findIndex(s => s[itemKey] === item[itemKey])
            if (index > -1) result.push(item)
          })
        } else if (this.filterToggle == filterToggles.available) {
          searchResult.forEach(item => {
            if (!this.isUnavailable(item)) {
              result.push(item)
            }
          })
        } else if (this.filterToggle == filterToggles.unavailable) {
          searchResult.forEach(item => {
            if (this.isUnavailable(item)) {
              result.push(item)
            }
          })
        } else {
          result = searchResult
        }

        return result
      },
      update() {
        // Keep the selected analytes that are still available rather than clearing the
        // selection: the list is rebuilt on every data refresh (stations/media types change).
        const available = new Set(this.allAnalytes.map(analyte => analyte[this.selectionKey]))
        const kept = this.selected.filter(analyte => available.has(analyte[this.selectionKey]))
        if (kept.length !== this.selected.length) this.selected = kept

        this.makeItemsMetadata(this.allAnalytes, itemKey)
      },
      toggleAll() {
        if (this.filterToggle != filterToggles.selected && this.selected.length != this.availableAnalytes.length) {
          this.selected = this.availableAnalytes
        } else {
          this.untoggleAll()
        }
      },
      untoggleAll() {
        this.selected = []
      },
      toggleRow(props) {
        if ((this.isUnavailable((props.item)) && !props.selected) || this.disabled) return
        props.selected = !props.selected
      },
      toggleCurrentAnalytes() {
        let filteredItems = this.$refs.analytesTable.filteredItems
        this.selected = mergeDistinctObjectArrays(
          this.selected,
          filteredItems,
          this.selectionKey
        )
      },
      selectAnalyteNames(analyteNames) {
        if (!analyteNames || analyteNames.length == 0) {
          this.untoggleAll()
          this.filterToggle = this.filterToggles.available
          return
        }

        this.filterToggle = this.filterToggles.selected

        // Will not work if this.selectionKey changes
        let selected = analyteNames.map(analyteName => ({
          [this.selectionKey]: analyteName,
        }))

        let missingNames = analyteNames.filter(name => !this.allAnalytes.find(analyte => analyte.AnalyteName == name))
        this.missingAnalytes = missingNames

        this.selected = selected
      },
      changeSort(sortable, column) {
        if (!sortable) return
        if (this.pagination.sortBy === column) {
          this.pagination.descending = !this.pagination.descending
        } else {
          this.pagination.sortBy = column
          this.pagination.descending = false
        }
      },
      formatDate(date) {
        return date ? date.format(prettyDateTimeFormat) : ''
      },
      makeItemsMetadata(items, itemKey) {
        let result = {}
        items.forEach(item => {
          result[item[itemKey]] = getObjectValues(item, searchKey)
        })

        this.itemsMetadata = result
      },
      mounted() {
        this.update()
      },
    },
  }
</script>

<style>
  .clickable {
    cursor: pointer !important;
  }

  .switch {
    margin: 0 0 8px 0;
    padding: 0 0 0 14px;
    display: inline-block;
  }

  .switch>.v-input__control>.v-input__slot>label {
    font-size: 14px !important;
    font-weight: 500 !important;
  }

  .chipFilter>.v-chip__content {
    cursor: pointer;
  }

  .analyteTable .theme--light.v-table tbody tr[active] {
    background: #f5f3ff;
  }

  .analytes-table.five-items>.v-table__overflow {
    min-height: 297px;
    background: #fafafa;
  }

  .analytes-table.ten-items>.v-table__overflow {
    min-height: 537px;
    background: #fafafa;
  }

  .analytes-table.twenty-five-items>.v-table__overflow {
    min-height: 1257px;
    background: #fafafa;
  }
</style>